import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Clock, ArrowRight, CheckCircle } from "lucide-react";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Insurance() {
  const [clicking, setClicking] = useState(false);

  const handleInsuranceClick = async () => {
    setClicking(true);
    try {
      // Track the click with auth headers to capture logged-in user
      await fetch(`${API}/api/insurance/track-click`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify({ 
          referral_source: "insurance_page",
          timestamp: new Date().toISOString()
        })
      });
      
      // Redirect to the insurance partner page
      window.location.href = "/toolinsureco";
    } catch (error) {
      console.error("Failed to track click:", error);
      // Still redirect even if tracking fails
      window.location.href = "/toolinsureco";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 sm:h-10" />
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/check" className="hidden md:block">
              <Button variant="outline" className="border-yellow-500 text-yellow-400">
                Check a Tool
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost">Login</Button>
            </Link>
            <Link to="/register">
              <Button className="btn-primary">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-4xl mx-auto">
        {/* Coming Soon Banner */}
        <div className="text-center mb-8">
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-lg px-4 py-2 mb-4">
            <Clock className="w-5 h-5 mr-2 inline" />
            Coming Soon
          </Badge>
          <h1 className="text-3xl md:text-4xl font-heading font-bold uppercase mb-4">
            Tool <span className="text-primary">Insurance</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Protect your valuable tools with exclusive insurance deals through our trusted partners.
          </p>
        </div>

        {/* Coming Soon Graphic */}
        <Card className="border-2 border-dashed border-primary/30 bg-primary/5 mb-8">
          <CardContent className="p-8 md:p-12 text-center">
            <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-16 h-16 text-primary" />
            </div>
            <h2 className="text-2xl font-heading font-bold mb-4">
              Insurance Partnership Coming Soon
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              We're working with leading tool insurance providers to bring you exclusive discounts 
              for TOOLIO members. Register your tools now to be first in line!
            </p>
            
            {/* Benefits Preview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 text-left">
              <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Exclusive Discounts</p>
                  <p className="text-sm text-muted-foreground">Special rates for TOOLIO users</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Easy Claims</p>
                  <p className="text-sm text-muted-foreground">Generate instant insurance reports with all your tool details, photos & proof of ownership</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Lower Premiums</p>
                  <p className="text-sm text-muted-foreground">TOOLIO reduces insurer risk - registered tools are more likely to be recovered by police</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Link Section */}
        <Card className="bg-slate-800 border-blue-900">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-lg font-bold text-white mb-1">
                  Preview: ToolInsureCo Partnership
                </h3>
                <p className="text-blue-300 text-sm">
                  Test our insurance referral system (demo partner page)
                </p>
              </div>
              <Button 
                onClick={handleInsuranceClick}
                disabled={clicking}
                className="btn-primary whitespace-nowrap"
              >
                {clicking ? "Redirecting..." : "Get a Quote"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Section */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            Want to be notified when insurance is available?{" "}
            <Link to="/register" className="text-primary hover:underline">
              Create a free account
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
